import React from 'react';
import { View, Text, TouchableOpacity, Image, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { fontFamily } from '../constants/fonts';
import { colors } from '../constants/theme';

/** Tailwind badge palette (not in shared theme) */
const badge = {
  blueBg: '#dbeafe',
  blueText: '#1e40af',
  greenBg: '#dcfce7',
  greenText: '#166534',
  orangeBg: '#ffedd5',
  orangeText: '#9a3412',
};

// Helper component for DISGO Pass logo with dashed outline
const DisgoPassLogo = () => (
  <View style={styles.logoOuter}>
    <View style={styles.logoInner}>
      <Image
        source={require('../assets/DisgoLogoBlack.png')}
        style={styles.logoImage}
        resizeMode="contain"
      />
      <Text style={styles.logoPassLabel}>
        Pass
      </Text>
    </View>
  </View>
);

// Helper functions for pass formatting
const formatTimeRange = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  
  // Convert Unix timestamps to readable time strings
  const startTimeStr = new Date(startTime * 1000).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  const endTimeStr = new Date(endTime * 1000).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  return `${startTimeStr}-${endTimeStr}`;
};

const getPassDays = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  
  const startDate = new Date(startTime * 1000);
  const endDate = new Date(endTime * 1000);
  
  // If start and end are on the same day
  if (startDate.toDateString() === endDate.toDateString()) {
    return startDate.toLocaleDateString('en-US', { weekday: 'long' });
  }
  
  // If they span multiple days, show the range
  const startDay = startDate.toLocaleDateString('en-US', { weekday: 'short' });
  const endDay = endDate.toLocaleDateString('en-US', { weekday: 'short' });
  
  return `${startDay}-${endDay}`;
};

const getTierRequirement = (tierRequired) => {
  if (!tierRequired) return '';
  return `Loyalty Tiers • ${tierRequired}`;
};

// Helper function to check if current time is within pass timeframe
const isWithinPassTimeframe = (pass, currentDatabaseTime) => {
  if (!currentDatabaseTime) {
    return false;
  }
  
  // First check if the pass is active (default to true if not specified)
  if (pass.isActive === false) {
    return false;
  }
  
  const now = new Date(currentDatabaseTime);
  
  // If no time constraints, the pass is always active (within any week constraints)
  const hasTimeConstraints = pass.startTime && pass.endTime;
  const hasWeekConstraints = pass.weekStart;
  
  // If no constraints at all, pass is always active
  if (!hasTimeConstraints && !hasWeekConstraints) {
    return true;
  }
  
  // Check if current time is within the time window
  if (hasTimeConstraints) {
    // First check if we're on the correct day of the week
    const startDate = new Date(pass.startTime * 1000);
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const passDay = startDate.toLocaleDateString('en-US', { weekday: 'long' });
    
    // If we're not on the correct day, the pass is not active
    if (currentDay !== passDay) {
      return false;
    }
    // Convert Unix timestamps to minutes for proper comparison
    const unixTimestampToMinutes = (timestamp) => {
      const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
      return date.getHours() * 60 + date.getMinutes();
    };

    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const startMinutes = unixTimestampToMinutes(pass.startTime);
    const endMinutes = unixTimestampToMinutes(pass.endTime);
    
    // Handle cases where the time window spans midnight
    if (endMinutes < startMinutes) {
      // Time window spans midnight (e.g., 11 PM to 2 AM)
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    } else {
      // Normal time window within the same day
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    }
  }
  
  return true;
};

const PassCard = ({ 
  pass, 
  showDistance = false, 
  distance = null,
  showRestaurantName = false,
  restaurantName = null,
  showClaimStatus = false,
  isClaimedThisWeek = false,
  isGiftedThisWeek = false,
  isDownThisWeek = false,
  actionType = null,
  showActionButtons = false,
  onGiftPress = null,
  onClaimPress = null,
  onUsePassPress = null,
  onCantGoPress = null,
  isDisgoPass = false,
  onRestaurantInfoPress = null
}) => {
  // Get current database time from backend
  const currentDatabaseTime = useQuery(api.mockData.getCurrentDatabaseTime);
  
  // Fallback to current device time if database time is not available
  const effectiveTime = currentDatabaseTime || new Date().toISOString();
  
  // Check if the pass is within its valid timeframe
  const isWithinTimeframe = isWithinPassTimeframe(pass, effectiveTime);
  
  // A pass is claimable if it's within timeframe AND the user can access it
  // For now, we'll use the timeframe check, but this could be enhanced to include tier/points checks
  const isPassClaimable = isWithinTimeframe;

  const cardBg =
    isClaimedThisWeek && actionType === 'down'
      ? 'rgba(156, 107, 66, 0.1)'
      : '#fafafa';

  return (
    <View style={styles.root}>
      <View
        style={[
          styles.cardOuter,
          {
            backgroundColor: cardBg,
            borderTopColor: colors.gray200,
            borderLeftColor: colors.gray200,
            borderRightColor: colors.gray300,
            borderBottomColor: colors.gray400,
          },
        ]}
      >
        {/* Pass Card */}
        <View style={styles.cardInner}>
          {/* Header Row */}
          <View style={styles.headerRow}>
            {/* Left Icon Area */}
            <View style={styles.headerIconWrap}>
              <Ionicons 
                name={pass.icon && pass.icon !== "🎁" ? pass.icon : "gift"} 
                size={20} 
                color={colors.disgoBrown} 
              />
            </View>
            
            {/* Status Badges */}
            <View style={styles.badgeRow}>
              {actionType === "shared" ? (
                <View style={[styles.badgePill, { backgroundColor: badge.blueBg }]}>
                  <Text style={[styles.badgeText, { color: badge.blueText }]}>
                    🎁 Gifted
                  </Text>
                </View>
              ) : actionType === "claimed" ? (
                <View style={[styles.badgePill, { backgroundColor: badge.greenBg }]}>
                  <Text style={[styles.badgeText, { color: badge.greenText }]}>
                    ✓ Claimed
                  </Text>
                </View>
              ) : actionType === "down" ? (
                <View style={[styles.badgePill, { backgroundColor: badge.orangeBg }]}>
                  <Text style={[styles.badgeText, { color: badge.orangeText }]}>
                    📅 Reserved
                  </Text>
                </View>
              ) : null}
            </View>
            
            {/* Right DISGO Pass Logo */}
            <DisgoPassLogo />
          </View>
          
          {/* Main Content Area */}
          <View style={styles.mainContent}>
            {/* Main Offer Text */}
            <Text style={styles.titleText}>
              {pass.title || ""}
            </Text>
            
            {/* Restaurant Name */}
            {showRestaurantName && restaurantName && (
              <View style={styles.metaRowTight}>
                <Text style={styles.metaBold}>
                  {restaurantName}
                </Text>
                {isDisgoPass && onRestaurantInfoPress && (
                  <TouchableOpacity 
                    onPress={() => onRestaurantInfoPress(pass)}
                    style={styles.infoBtn}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons 
                      name="information-circle-outline" 
                      size={20} 
                      color={colors.disgoBrown} 
                    />
                  </TouchableOpacity>
                )}
              </View>
            )}
            
            {/* Day and Time */}
            {pass.startTime && pass.endTime && (
              <View style={styles.metaRowTight}>
                <Text style={styles.metaBold}>
                  {getPassDays(pass.startTime, pass.endTime)}
                </Text>
                <Text style={styles.metaRegular}>
                  • {formatTimeRange(pass.startTime, pass.endTime)}
                </Text>
              </View>
            )}
            
            {/* Loyalty Tier */}
            {pass.tierRequired && (
              <View style={styles.metaRow}>
                <Text style={styles.metaBold}>
                  Loyalty Tiers •
                </Text>
                <Text style={styles.metaRegular}>
                  {pass.tierRequired}+
                </Text>
              </View>
            )}
          </View>
          
          {/* Action Buttons */}
          {showActionButtons && onGiftPress && onClaimPress && (
            <View style={styles.actionRow}>
              {actionType === "down" && onUsePassPress && onCantGoPress ? (
                <>
                  {isPassClaimable && (
                    <TouchableOpacity 
                      onPress={() => onUsePassPress(pass)}
                      style={[styles.actionBtnFlex, styles.usePassLeft]}
                    >
                      <Text style={styles.actionBtnTextLight}>
                        use pass
                      </Text>
                    </TouchableOpacity>
                  )}
                  
                  <TouchableOpacity 
                    onPress={() => onCantGoPress(pass)}
                    style={[
                      styles.actionBtnFlex,
                      styles.cantGoBg,
                      isPassClaimable ? styles.cantGoRight : styles.cantGoSolo,
                    ]}
                  >
                    <Text style={styles.actionBtnTextMuted}>
                      can't go
                    </Text>
                  </TouchableOpacity>
                </>
              ) : actionType === "shared" && onUsePassPress ? (
                isPassClaimable ? (
                  <TouchableOpacity 
                    onPress={() => onUsePassPress(pass)}
                    style={[styles.actionBtnFlex, styles.redeemDarkFull]}
                  >
                    <Text style={styles.actionBtnTextLight}>
                      use pass
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity 
                    onPress={() => onClaimPress(pass, isWithinTimeframe)}
                    style={[styles.actionBtnFlex, styles.redeemBrownFull]}
                  >
                    <Text style={styles.actionBtnTextLight}>
                      Reserve
                    </Text>
                  </TouchableOpacity>
                )
              ) : !isClaimedThisWeek ? (
                <>
                  <TouchableOpacity 
                    onPress={() => onGiftPress(pass, restaurantName)}
                    style={[styles.actionBtnFlex, styles.giftLeft]}
                  >
                    <Text style={styles.actionBtnTextLight}>
                      gift it
                    </Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity 
                    onPress={() => onClaimPress(pass, isWithinTimeframe)}
                    style={[
                      styles.actionBtnFlex,
                      styles.redeemRight,
                      isPassClaimable ? styles.redeemDark : styles.redeemBrown,
                    ]}
                  >
                    <Text style={styles.actionBtnTextLight}>
                      {isPassClaimable ? "Use pass" : "Reserve"}
                    </Text>
                  </TouchableOpacity>
                </>
              ) : null}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    marginBottom: 12,
  },
  cardOuter: {
    backgroundColor: colors.white,
    borderWidth: 2,
    borderColor: colors.gray300,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 15,
  },
  cardInner: {
    paddingTop: 0,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 0,
  },
  headerIconWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 16,
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badgePill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 9999,
  },
  badgeText: {
    fontSize: 12,
    fontFamily: fontFamily.abhayaBold,
  },
  mainContent: {
    marginLeft: 16,
    marginTop: -20,
    marginBottom: 8,
  },
  titleText: {
    fontSize: 20,
    fontFamily: fontFamily.abhayaBold,
    color: colors.brownDark,
    marginTop: 0,
  },
  metaRowTight: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaBold: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
  },
  metaRegular: {
    fontSize: 16,
    fontFamily: fontFamily.abhaya,
    color: colors.disgoBrown,
    marginLeft: 4,
  },
  infoBtn: {
    marginLeft: 4,
    padding: 2,
  },
  actionRow: {
    flexDirection: 'row',
  },
  actionBtnFlex: {
    flex: 1,
    paddingVertical: 12,
  },
  actionBtnTextLight: {
    textAlign: 'center',
    color: colors.white,
    fontFamily: fontFamily.abhayaBold,
    fontSize: 16,
  },
  actionBtnTextMuted: {
    textAlign: 'center',
    color: colors.gray500,
    fontFamily: fontFamily.abhayaBold,
    fontSize: 16,
  },
  usePassLeft: {
    backgroundColor: colors.disgoBrown,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    marginRight: 2,
  },
  cantGoBg: {
    backgroundColor: '#D4C4B7',
  },
  cantGoRight: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    marginLeft: 2,
  },
  cantGoSolo: {
    borderRadius: 8,
  },
  redeemDarkFull: {
    borderRadius: 8,
    backgroundColor: '#4A3428',
  },
  redeemBrownFull: {
    borderRadius: 8,
    backgroundColor: colors.disgoBrown,
  },
  giftLeft: {
    backgroundColor: '#9C6B42',
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    marginRight: 2,
  },
  redeemRight: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    marginLeft: 2,
  },
  redeemDark: {
    backgroundColor: '#4A3428',
  },
  redeemBrown: {
    backgroundColor: colors.disgoBrown,
  },
  logoOuter: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoInner: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    position: 'relative',
  },
  logoImage: {
    width: 64,
    height: 64,
  },
  logoPassLabel: {
    position: 'absolute',
    top: 42,
    left: 42,
    fontSize: 12,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
    textAlign: 'center',
  },
});

export default PassCard;
